'use client';

import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  success?: boolean;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
  variant?: 'default' | 'outline' | 'filled';
  size?: 'sm' | 'md' | 'lg';
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  success,
  icon,
  rightIcon,
  showPasswordToggle = false,
  variant = 'default',
  size = 'md',
  className = '',
  type = 'text',
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password') 
    : type;

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg'
  };

  const variantClasses = {
    default: `
      bg-gradient-to-r from-gray-900/80 to-gray-800/80
      border-2 border-transparent
      focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20
      hover:border-gray-600
    `,
    outline: `
      bg-transparent
      border-2 border-gray-600
      focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20
      hover:border-gray-500
    `,
    filled: `
      bg-gray-800/60
      border-2 border-transparent
      focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20
      hover:bg-gray-800/80
    `
  };

  const getStatusIcon = () => {
    if (success) {
      return <FaCheck className="text-green-500" />;
    }
    if (error) {
      return <FaTimes className="text-red-500" />;
    }
    return null;
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-yellow-400 text-sm font-semibold mb-2 tracking-wide">
          {label}
        </label>
      )}
      <div className="relative group">
        {/* Left Icon */}
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
            <div className="text-gray-400 group-focus-within:text-yellow-500 transition-colors duration-300">
              {icon}
            </div>
          </div>
        )}

        {/* Input Field */}
        <input
          type={inputType}
          className={`
            w-full rounded-xl
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            text-white placeholder-gray-400
            focus:outline-none
            transition-all duration-300 ease-in-out
            ${icon ? 'pl-12' : 'pl-4'}
            ${rightIcon || showPasswordToggle || getStatusIcon() ? 'pr-12' : 'pr-4'}
            ${error ? 'border-red-500 ring-2 ring-red-500/20' : ''}
            ${success ? 'border-green-500 ring-2 ring-green-500/20' : ''}
            ${isFocused ? 'transform scale-[1.02]' : ''}
            backdrop-blur-sm
            shadow-lg
            ${className}
          `}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />

        {/* Right Icons */}
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center space-x-2">
          {/* Status Icon */}
          {getStatusIcon() && (
            <div className="flex items-center">
              {getStatusIcon()}
            </div>
          )}

          {/* Password Toggle */}
          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-yellow-500 transition-colors duration-300 focus:outline-none"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          )}

          {/* Custom Right Icon */}
          {rightIcon && (
            <div className="text-gray-400 group-focus-within:text-yellow-500 transition-colors duration-300">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Focus Ring Effect */}
        {isFocused && (
          <div className="absolute inset-0 rounded-xl ring-2 ring-yellow-500/30 pointer-events-none animate-pulse"></div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center space-x-2">
          <FaTimes className="text-red-500 text-xs" />
          <p className="text-sm text-red-400 font-medium">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && !error && (
        <div className="mt-2 flex items-center space-x-2">
          <FaCheck className="text-green-500 text-xs" />
          <p className="text-sm text-green-400 font-medium">Looks good!</p>
        </div>
      )}
    </div>
  );
};

export default Input;
